import { useState } from 'react';
import { FaTrash, FaTimes } from 'react-icons/fa';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import { compressImageFile, IMAGE_ACCEPT } from '../../../utils/imageCompression';

function NotesAndPhotosSection({ notes, onNotesChange, photos, onPhotosChange }) {
    const [uploading, setUploading] = useState(false);
    const [lightboxUrl, setLightboxUrl] = useState(null);

    const handleAddPhotos = async (event) => {
        const files = Array.from(event.target.files || []);
        event.target.value = '';
        if (files.length === 0) return;

        setUploading(true);
        try {
            // Compression une par une (et non en parallèle) : la conversion
            // HEIC décode des images de 12 Mpx, plusieurs à la fois saturent
            // la mémoire du téléphone. Une photo illisible donne null.
            const images = [];
            for (const file of files) {
                images.push(await compressImageFile(file).catch((err) => {
                    console.error("Lecture de la photo impossible :", err);
                    return null;
                }));
            }
            const uploadedUrls = await Promise.all(images.map(async (image) => {
                if (!image) return null;
                const formData = new FormData();
                // Blob compressé sans nom : un nom de fichier explicite est
                // nécessaire pour FormData/Cloudinary.
                formData.append('image', image, 'photo.jpg');
                const response = await fetch(`${process.env.REACT_APP_API_URL}/upload_image`, {
                    method: 'POST',
                    body: formData,
                });
                if (!response.ok) return null;
                const data = await response.json();
                return data.image_url || null;
            }));

            const successfulUrls = uploadedUrls.filter(Boolean);
            if (successfulUrls.length > 0) {
                onPhotosChange([...photos, ...successfulUrls]);
            }
            if (successfulUrls.length < files.length) {
                toast.error("Certaines photos n'ont pas pu être téléchargées.");
            }
        } catch (error) {
            console.error("Erreur lors du téléchargement des photos :", error);
            toast.error("Erreur de connexion à l'API");
        } finally {
            setUploading(false);
        }
    };

    const handleDeletePhoto = async (photoUrl) => {
        const result = await Swal.fire({
            title: 'Êtes-vous sûr ?',
            text: "Voulez-vous vraiment supprimer cette photo ? Cette action est irréversible.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Oui, supprimer',
            cancelButtonText: 'Non, annuler',
            customClass: 'swal-custom',
        });
        if (!result.isConfirmed) return;

        onPhotosChange(photos.filter((url) => url !== photoUrl));

        try {
            await fetch(`${process.env.REACT_APP_API_URL}/delete_image`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ image_url: photoUrl }),
            });
        } catch (error) {
            console.error("Erreur lors de la suppression de la photo :", error);
        }
    };

    return (
        <div className="notes-photos-section">
            <label htmlFor="entity-notes">Notes</label>
            <textarea
                id="entity-notes"
                className="entity-notes-textarea"
                rows={4}
                placeholder="Ajouter une note..."
                value={notes}
                onChange={(e) => onNotesChange(e.target.value)}
            />

            <label>Photos</label>
            <div className="room-photo-grid">
                {photos.map((url) => (
                    <div className="room-photo-thumb" key={url}>
                        <img src={url} alt="Pièce" onClick={() => setLightboxUrl(url)} />
                        <button
                            type="button"
                            className="room-photo-thumb-delete"
                            onClick={() => handleDeletePhoto(url)}
                        >
                            <FaTrash size={12} />
                        </button>
                    </div>
                ))}

                {/* `capture` ouvre directement l'appareil photo mais bloque la galerie :
                    la galerie reste accessible via le lien discret sous la grille. */}
                <label
                    htmlFor="entity-photos-camera"
                    className={`room-photo-add-tile${uploading ? ' room-photo-add-tile--disabled' : ''}`}
                    title="Prendre une photo"
                >
                    {uploading ? '…' : '+'}
                </label>
                <input
                    type="file"
                    id="entity-photos-camera"
                    accept={IMAGE_ACCEPT}
                    capture="environment"
                    disabled={uploading}
                    onChange={handleAddPhotos}
                    style={{ display: 'none' }}
                />
                <input
                    type="file"
                    id="entity-photos-gallery"
                    accept={IMAGE_ACCEPT}
                    multiple
                    disabled={uploading}
                    onChange={handleAddPhotos}
                    style={{ display: 'none' }}
                />
            </div>
            {!uploading && (
                <label htmlFor="entity-photos-gallery" className="photo-gallery-link">
                    ou choisir depuis la galerie
                </label>
            )}

            {lightboxUrl && (
                <div className="photo-lightbox-backdrop" onClick={() => setLightboxUrl(null)}>
                    <button type="button" className="photo-lightbox-close" onClick={() => setLightboxUrl(null)}>
                        <FaTimes size={20} />
                    </button>
                    <img src={lightboxUrl} alt="Agrandissement" className="photo-lightbox-img" onClick={(e) => e.stopPropagation()} />
                </div>
            )}
        </div>
    );
}

export default NotesAndPhotosSection;
